<template>
  <div class="playersInforRadar" ref="playersInforRadar">
    <div class="playerItem home">
      <AbPlayerImg
        :playerImg="homePlayer.profilePicture"
        :playerNum="homePlayer.shirtNumber * 1"
        :playerName="homePlayer.playerName"
        :countryImg="homeInfo.teamLogo || homePlayer.countryLogo"
        :countryName="homeInfo.teamName || homePlayer.countryName"
        :width="'100%'"
      />
    </div>
    <div class="themeData">
      <el-select
        :popper-append-to-body="false"
        placeholder="请选择"
        class="line-select"
        v-model="lineValue"
        clearable
        @change="handleLineChange"
      >
        <el-option
          v-for="(item, index) in lineOptions"
          :key="index"
          :value="item"
        >
          <div :style="{ backgroundColor: item }"></div>
        </el-option>
      </el-select>
      <div class="radar" ref="container"></div>
    </div>
  </div>
</template>

<script>
// 球员数据对比 主题
import AbPlayerImg from '@components/compreSportsMeet/html/components/PlayerImg'
import canvasToImage_base64_data_mixin from '@mixins/canvasToImage_base64_data'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import {
  CSS2DRenderer,
  CSS2DObject,
} from 'three/examples/jsm/renderers/CSS2DRenderer.js'

const colors = [
  '#E996B0',
  '#D9F4AD',
  '#4444ff',
  '#ffff44',
  '#44ffff',
  '#ff44ff',
  '#ff8844',
  '#88ff44',
  '#4488ff',
]
export default {
  name: 'playersInforRadar',
  mixins: [canvasToImage_base64_data_mixin],
  components: { AbPlayerImg },
  props: {
    datas: {
      // 必传参数，球员的所有信息放到一个对象里
      type: Object,
      default: () => ({
        title: '',
        objectInfo: [],
        data: {
          dataList: [],
        },
        statisticsModels: [],
      }),
    },
    isAutoExportPng: {
      //是否导出图片页面使用
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      lineValue: '',
      lineOptions: [],
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      trajectories: {},
    }
  },
  computed: {
    objectInfo() {
      return this.datas?.objectInfo || []
    },
    homeInfo() {
      return this.objectInfo[0] || {}
    },
    homePlayer() {
      return this.homeInfo?.playerInfos?.[0] || {}
    },
    dataList() {
      return this.datas?.data?.dataList || []
    },
  },
  watch: {
    dataList(val) {
      this.draw(val)
    },
  },
  mounted() {
    this.init()
    this.draw(this.dataList)
    window.addEventListener('resize', this.onWindowResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize)
    if (this.animationId) cancelAnimationFrame(this.animationId)
    if (this.labelRenderer && this.labelRenderer.domElement) {
      this.labelRenderer.domElement.remove()
    }
    this.labelRenderer = null
    if (this.controls) this.controls.dispose()
    if (this.renderer) this.renderer.dispose()

    if (this.scene) {
      this.scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose()
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach((m) => m.dispose())
          } else {
            obj.material.dispose()
          }
        }
      })
      this.scene.clear()
    }
  },
  methods: {
    init() {
      const container = this.$refs.container
      if (!container) return
      const scene = new THREE.Scene()
      this.scene = scene

      this.camera = new THREE.PerspectiveCamera(
        25,
        container.clientWidth / container.clientHeight,
        0.1,
        1000
      )
      this.camera.position.set(0, 35, 38)

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
      this.renderer.setSize(container.clientWidth, container.clientHeight)
      this.renderer.setPixelRatio(window.devicePixelRatio)
      container.appendChild(this.renderer.domElement)
      this.scene.background = null

      // CSS2D 标签渲染器
      this.labelRenderer = new CSS2DRenderer()
      this.labelRenderer.setSize(container.clientWidth, container.clientHeight)
      Object.assign(this.labelRenderer.domElement.style, {
        position: 'absolute',
        top: '0px',
        left: '0px',
        pointerEvents: 'none',
      })
      container.appendChild(this.labelRenderer.domElement)

      // ========== 灯光 ==========
      const ambient = new THREE.AmbientLight(0xffffff, 0.8)
      this.scene.add(ambient)

      const dirLight = new THREE.DirectionalLight(0xffffff, 0.6)
      dirLight.position.set(10, 20, 10)
      this.scene.add(dirLight)

      // ========== 轨道控制：左键旋转、右键平移、滚轮缩放 ==========
      this.controls = new OrbitControls(this.camera, this.renderer.domElement)
      this.controls.enableDamping = true
      this.controls.dampingFactor = 0.08
      this.controls.target.set(0, 0, 0)
      this.controls.maxPolarAngle = Math.PI / 2 - 0.05

      // ========== 网球场 ==========
      const RUNBACK = 6.4 // 端线后缓冲区
      const RUNOFF = 3.66 // 边线外缓冲区
      const COURT_W = 10.97 // 双打宽度
      const COURT_H = 23.77 // 长度
      const SINGLE_W = 8.23 // 单打宽度
      const SERVICE_Z = 6.4 // 发球线到球网距离
      const HALF_H = COURT_H / 2
      const HALF_W = COURT_W / 2
      const HALF_SINGLE = SINGLE_W / 2
      const TOTAL_W = COURT_W + RUNOFF * 2 // 18.29
      const TOTAL_H = COURT_H + RUNBACK * 2 // 36.57

      // 地面
      const groundGeo = new THREE.PlaneGeometry(TOTAL_W, TOTAL_H)
      const groundMat = new THREE.MeshStandardMaterial({ color: '#337047' })
      const ground = new THREE.Mesh(groundGeo, groundMat)
      ground.rotation.x = -Math.PI / 2
      this.scene.add(ground)

      // 球场
      const courtGeo = new THREE.PlaneGeometry(COURT_W, COURT_H)
      const courtMat = new THREE.MeshStandardMaterial({ color: '#426494' })
      const court = new THREE.Mesh(courtGeo, courtMat)
      court.rotation.x = -Math.PI / 2
      court.position.y = 0.01
      this.scene.add(court)

      // 画线辅助
      function addLine(x1, z1, x2, z2, color = 0xffffff) {
        const points = [
          new THREE.Vector3(x1, 0.02, z1),
          new THREE.Vector3(x2, 0.02, z2),
        ]
        const geo = new THREE.BufferGeometry().setFromPoints(points)
        const mat = new THREE.LineBasicMaterial({ color })
        scene.add(new THREE.Line(geo, mat))
      }

      // 外框
      addLine(-HALF_W, -HALF_H, HALF_W, -HALF_H)
      addLine(HALF_W, -HALF_H, HALF_W, HALF_H)
      addLine(HALF_W, HALF_H, -HALF_W, HALF_H)
      addLine(-HALF_W, HALF_H, -HALF_W, -HALF_H)

      // 单打边线
      addLine(-HALF_SINGLE, -HALF_H, -HALF_SINGLE, HALF_H)
      addLine(HALF_SINGLE, -HALF_H, HALF_SINGLE, HALF_H)

      // 发球线
      addLine(-HALF_SINGLE, -SERVICE_Z, HALF_SINGLE, -SERVICE_Z)
      addLine(-HALF_SINGLE, SERVICE_Z, HALF_SINGLE, SERVICE_Z)

      // 中线
      addLine(0, -HALF_H, 0, -SERVICE_Z)
      addLine(0, SERVICE_Z, 0, HALF_H)

      // 球网
      const netGeo = new THREE.BoxGeometry(COURT_W + 0.4, 0.91, 0.05)
      const netMat = new THREE.MeshStandardMaterial({ color: 0xcccccc })
      const net = new THREE.Mesh(netGeo, netMat)
      net.position.y = 0.91 / 2
      this.scene.add(net)

      // 网柱
      const postGeo = new THREE.CylinderGeometry(0.06, 0.06, 1.05)
      const postMat = new THREE.MeshStandardMaterial({ color: 0x888888 })
      const leftPost = new THREE.Mesh(postGeo, postMat)
      leftPost.position.set(-HALF_W - 0.2, 1.05 / 2, 0)
      this.scene.add(leftPost)
      const rightPost = new THREE.Mesh(postGeo, postMat)
      rightPost.position.set(HALF_W + 0.2, 1.05 / 2, 0)
      this.scene.add(rightPost)

      this.animate()
    },

    randomHexColor() {
      const r = Math.floor(Math.random() * 256)
      const g = Math.floor(Math.random() * 256)
      const b = Math.floor(Math.random() * 256)
      const r1 = r.toString(16).padStart(2, '0')
      const g1 = g.toString(16).padStart(2, '0')
      const b1 = b.toString(16).padStart(2, '0')

      return `#${r1}${g1}${b1}`
    },

    handleLineChange(data) {
      Object.keys(this.trajectories).forEach((color) => {
        if (!data) {
          this.trajectories[color].line.visible = true
          this.trajectories[color].speed.visible = false
          this.trajectories[color].height.visible = false
          this.trajectories[color].point.visible = true
        } else {
          if (color === data) {
            this.trajectories[color].line.visible = true
            this.trajectories[color].speed.visible = true
            this.trajectories[color].height.visible = true
            this.trajectories[color].point.visible = true
          } else {
            this.trajectories[color].line.visible = false
            this.trajectories[color].speed.visible = false
            this.trajectories[color].height.visible = false
            this.trajectories[color].point.visible = false
          }
        }
      })
    },

    draw(data) {
      this.lineOptions = []
      this.lineValue = ''
      Object.values(this.trajectories).forEach((item) => {
        this.scene.remove(item.line)
        this.scene.remove(item.speed)
        this.scene.remove(item.height)
        this.scene.remove(item.point)
      })
      this.trajectories = {}
      if (!data || data.length === 0) return

      data.forEach((item, index) => {
        const { netClearance, bouncePointY, bouncePointX } = item
        const color = colors[index] || this.randomHexColor()
        const start = item.points?.[0]
        const line = this.drawTrace(item.points || [], color)
        const point = this.drawPoint(
          { x: -bouncePointY, z: -bouncePointX },
          color
        )
        const speed = this.drawText(`${item.serveSpeed} km/h`, start, color)
        const center = (item.points || 0).find((point) => point.x == 0)
        let height
        if (center) {
          height = this.drawText(`过网 ${netClearance}cm`, center, color)
        }
        this.trajectories[color] = { line, speed, height, point }
        this.lineOptions.push(color)
      })
    },

    // 轨迹线
    drawTrace(data, color = 0xff4444) {
      if (data.length < 2) return
      const points = data.map((point) => {
        const x = -point.y
        const y = point.z
        const z = -point.x
        return new THREE.Vector3(x, y, z)
      })
      const geo = new THREE.BufferGeometry().setFromPoints(points)
      const mat = new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity: 0.85,
      })
      const line = new THREE.Line(geo, mat)
      this.scene.add(line)
      return line
    },

    // 落点
    drawPoint(point, color = 0xff4444) {
      const position = new THREE.Vector3(point.x, 0, point.z)
      const geometry = new THREE.RingGeometry(0.35, 0.5, 32)
      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.8,
        side: THREE.DoubleSide,
      })
      const circle = new THREE.Mesh(geometry, material)

      // 让圆圈平躺在球场平面上
      circle.rotation.x = -Math.PI / 2
      circle.position.copy(position)
      circle.position.y += 0.02 // 稍微抬高一点，避免和地面 z-fight

      this.scene.add(circle)
      return circle
    },

    // 文字
    drawText(text, point, color = '#ffffff') {
      if (!point) return
      const position = new THREE.Vector3(-point.y, point.z, -point.x)
      const labelDiv = document.createElement('div')
      labelDiv.textContent = text
      labelDiv.style.color = color
      labelDiv.style.fontSize = '14px'
      labelDiv.style.whiteSpace = 'nowrap'

      const label = new CSS2DObject(labelDiv)
      label.position.copy(position)
      label.visible = false
      this.scene.add(label)
      return label
    },

    animate() {
      this.animationId = requestAnimationFrame(this.animate)
      this.controls && this.controls.update()
      this.renderer.render(this.scene, this.camera)
      this.labelRenderer.render(this.scene, this.camera)
    },

    onWindowResize() {
      const container = this.$refs.container
      if (!container) return
      this.camera.aspect = container.clientWidth / container.clientHeight
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(container.clientWidth, container.clientHeight)
      this.labelRenderer.setSize(container.clientWidth, container.clientHeight)
    },

    createTextSprite(text, color = '#ffffff') {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      const fontSize = 12
      const padding = 0

      ctx.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`
      const width = ctx.measureText(text).width + padding * 2
      const height = fontSize + padding * 2

      canvas.width = width
      canvas.height = height

      ctx.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = color
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(text, width / 2, height / 2)

      const texture = new THREE.CanvasTexture(canvas)
      texture.colorSpace = THREE.SRGBColorSpace

      const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: true,
        depthWrite: false,
      })

      const sprite = new THREE.Sprite(material)
      sprite.scale.set(width / 20, height / 20, 1) // 控制显示大小
      return sprite
    },

    addLandingCircle(position, color) {
      const geometry = new THREE.RingGeometry(0.35, 0.5, 32)
      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.8,
        side: THREE.DoubleSide,
      })
      const circle = new THREE.Mesh(geometry, material)

      // 让圆圈平躺在球场平面上
      circle.rotation.x = -Math.PI / 2
      circle.position.copy(position)
      circle.position.y += 0.02 // 稍微抬高一点，避免和地面 z-fight

      scene.add(circle)
    },
  },
}
</script>

<style lang="scss" scoped>
.playersInforRadar {
  position: relative;
  width: 100%;
  height: 22vw;
  padding: 0 3%;
  display: flex;
  display: -webkit-flex;
  justify-content: flex-start;
  align-items: center;
  .playerItem {
    display: flex;
    display: -webkit-flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    width: 6.94vw;
  }
  .themeData {
    width: calc(100% - 6.94vw);
    height: 100%;
    display: flex;
    display: -webkit-flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding: 0 2% 2%;
    .line-select {
      margin-top: 4px;
      height: 2vw;
      ::v-deep(.el-input__inner) {
        height: 2vw;
      }
      ::v-deep(.el-select-dropdown__item) {
        height: 2vw;
        line-height: 2vw;
        display: flex;
        align-items: center;
        & > div {
          height: 2px;
          width: 100%;
        }
      }
    }
    .radar {
      width: 100%;
      flex: 1;
      transform: rotateX(0deg);
      position: relative;
      // background: #27568e;
    }
  }
}
.mixtmt_ipad .playersInforRadar {
  padding: 0 2%;
  .themeData {
    .echart_dom {
      width: 85%;
    }
  }
}
</style>
